/** 검토 — the campaign drafts waiting for a decision (S5).
 *
 *  pending_review is the working queue; judge_rejected (the ad-likeness judge
 *  turned them down after its retries) and rejected (a person did) are kept
 *  for a second look. A draft that is approved leaves this page for the
 *  schedule, where it has a slot. Ported from the old Drafts page, which also
 *  held the scheduled, published and failed drafts: those are 일정 and 결과
 *  now. */

import { useMemo, useState } from 'react'
import { Check, Inbox, PanelRightOpen, PenLine, RotateCcw, Sparkles, X } from 'lucide-react'
import type { Draft } from '@/api/client'
import { usePostActions } from '@/lib/postActions'
import { useCampaigns, useDraftCounts, useDrafts, useTargets } from '@/lib/queries'
import { postUrlError } from '@/lib/postUrl'
import { formatShort } from '@/lib/time'
import { cn } from '@/lib/utils'
import {
  Button,
  ButtonLink,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  FilterBar,
  FilterSelect,
  Input,
  PageHeader,
  PostPreview,
  Segmented,
  SkeletonRows,
  StatusPill,
  Textarea,
  errorText,
  useQueryParam,
  useToast,
} from '@/ui'
import { autoApproves, coupangMetaOf } from '@/lib/coupang'
import { AutoApproveNote, CoupangPost } from './CoupangDraft'
import { DraftEditor, DraftForm, judgeIsStale, parseMediaIds, parseMeta } from '../schedule/DraftForm'
import { useOpenDetail } from '../schedule/shared'
import { GenerateNowDialog } from './GenerateNowDialog'

export type ReviewStatus = 'pending_review' | 'judge_rejected' | 'rejected'

const STATUSES: ReviewStatus[] = ['pending_review', 'judge_rejected', 'rejected']

const STATUS_LABEL: Record<ReviewStatus, string> = {
  pending_review: '검토 대기',
  judge_rejected: '심사 탈락',
  rejected: '반려',
}

const SORTS = [
  { value: '', label: '캠페인·생성순' },
  { value: 'score_asc', label: '광고 점수 낮은 순' },
  { value: 'score_desc', label: '광고 점수 높은 순' },
]

/** The judge's bands, as the old card had them: under 4 reads as a person,
 *  under 7 as borderline, the rest as an ad. */
export function judgeTone(score: number): 'published' | 'publishing' | 'failed' {
  return score < 4 ? 'published' : score < 7 ? 'publishing' : 'failed'
}

const FILL = { published: 'bg-status-published', publishing: 'bg-status-publishing', failed: 'bg-status-failed' }
const TEXT = { published: 'text-status-published', publishing: 'text-status-publishing', failed: 'text-status-failed' }

export default function Review() {
  const [statusParam, setStatus] = useQueryParam('status', 'pending_review')
  const [platform, setPlatform] = useQueryParam('platform')
  const [campaignParam, setCampaignParam] = useQueryParam('campaign_id')
  const [subreddit, setSubreddit] = useQueryParam('subreddit')
  const [sort, setSort] = useQueryParam('sort')
  const [writing, setWriting] = useState(false)
  const [generating, setGenerating] = useState(false)
  const status: ReviewStatus = (STATUSES as string[]).includes(statusParam) ? (statusParam as ReviewStatus) : 'pending_review'
  const campaignId = Number(campaignParam) || 0

  const campaigns = useCampaigns().data?.items ?? []
  const filter = { campaignId: campaignId || undefined, platform: platform || undefined }
  // The subreddit is applied below rather than on the server, so its select
  // keeps listing every channel in the queue instead of only the one picked.
  const list = useDrafts(status, filter, true)
  const counts = useDraftCounts(filter, true).data?.counts ?? {}
  const items = list.data?.items ?? []

  const channels = useMemo(
    () => Array.from(new Set(items.map((d) => String(parseMeta(d).subreddit ?? '')).filter(Boolean))).sort(),
    [items],
  )
  const shown = useMemo(() => {
    const rows = subreddit ? items.filter((d) => parseMeta(d).subreddit === subreddit) : items.slice()
    if (sort === 'score_asc') return rows.sort((a, b) => a.JudgeScore - b.JudgeScore)
    if (sort === 'score_desc') return rows.sort((a, b) => b.JudgeScore - a.JudgeScore)
    return rows.sort(
      (a, b) =>
        (a.CampaignName ?? '').localeCompare(b.CampaignName ?? '') || String(a.CreatedAt).localeCompare(String(b.CreatedAt)) || a.ID - b.ID,
    )
  }, [items, subreddit, sort])

  const elsewhere = ['scheduled', 'approved', 'published', 'failed'].reduce((n, k) => n + (counts[k] ?? 0), 0)

  return (
    <>
      <PageHeader
        title="검토"
        description="캠페인이 만든 글을 올리기 전에 본다. 손으로 쓴 리딤 홍보글은 일정에서 다룬다."
        actions={
          <>
            <Button icon={Sparkles} onClick={() => setGenerating(true)}>
              AI 초안 쓰기
            </Button>
            <Button variant="primary" icon={PenLine} onClick={() => setWriting(true)}>
              글 쓰기
            </Button>
          </>
        }
      >
        <FilterBar end={<span className="text-meta tabular-nums text-muted-foreground">{shown.length}건</span>}>
          <Segmented<ReviewStatus>
            label="상태"
            value={status}
            onChange={setStatus}
            options={STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s], count: counts[s] ?? 0 }))}
          />
          <FilterSelect
            label="캠페인"
            value={campaignId ? String(campaignId) : ''}
            onChange={setCampaignParam}
            options={[{ value: '', label: '캠페인 전체' }, ...campaigns.map((c) => ({ value: String(c.ID), label: c.Name }))]}
          />
          <FilterSelect
            label="플랫폼"
            value={platform}
            onChange={setPlatform}
            options={[
              { value: '', label: '플랫폼 전체' },
              { value: 'reddit', label: 'Reddit' },
              { value: 'threads', label: 'Threads' },
            ]}
          />
          <FilterSelect
            label="서브레딧"
            value={subreddit}
            onChange={setSubreddit}
            options={[{ value: '', label: '채널 전체' }, ...channels.map((c) => ({ value: c, label: `r/${c}` }))]}
          />
          <FilterSelect label="정렬" value={sort} onChange={setSort} options={SORTS} />
        </FilterBar>
      </PageHeader>

      {list.error ? (
        <ErrorState title="검토 목록을 불러오지 못했다" error={list.error} onRetry={() => void list.refetch()} />
      ) : list.isLoading ? (
        <SkeletonRows rows={4} />
      ) : shown.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title={status === 'pending_review' ? '검토할 글이 없다' : `${STATUS_LABEL[status]}한 글이 없다`}
          description={
            elsewhere > 0
              ? `예약·게시·실패 ${elsewhere}건은 일정과 결과에 있다.`
              : '캠페인이 글을 만들면 여기에 쌓인다. 직접 쓰거나 AI 초안을 쓸 수도 있다.'
          }
          actions={
            <>
              {STATUSES.filter((s) => s !== status && (counts[s] ?? 0) > 0).map((s) => (
                <Button key={s} onClick={() => setStatus(s)}>
                  {STATUS_LABEL[s]} {counts[s]}건
                </Button>
              ))}
              {elsewhere > 0 && (
                <ButtonLink to="/schedule?source=campaign" variant="secondary">
                  일정에서 보기
                </ButtonLink>
              )}
            </>
          }
        />
      ) : (
        <ul className="max-w-[720px] space-y-4" aria-label="검토할 글">
          {shown.map((d) => (
            <li key={d.ID}>
              <ReviewCard draft={d} />
            </li>
          ))}
        </ul>
      )}

      <Dialog open={writing} onClose={() => setWriting(false)} title="글 쓰기" size="lg"
        description="캠페인 검토 대기에 올라가는 글. 승인하면 캠페인 페이스에 맞춰 슬롯을 받는다.">
        <div className="max-h-[70vh] overflow-y-auto pb-2">
          <DraftForm onDone={() => setWriting(false)} />
        </div>
      </Dialog>
      <GenerateNowDialog open={generating} onClose={() => setGenerating(false)} initialCampaignId={campaignId || undefined} />
    </>
  )
}

function JudgeScore({ score, attempts, stale }: { score: number; attempts: number; stale?: boolean }) {
  const tone = judgeTone(score)
  return (
    <div
      className={cn('w-36 shrink-0 space-y-1', stale && 'opacity-60')}
      title={
        stale
          ? '고치기 전 글에 매긴 점수다. 고친 글은 다시 판정하지 않았다.'
          : '광고 같은 정도, 0~10. 4 미만은 사람 글, 7 미만은 경계, 그 위는 광고로 읽힌다.'
      }
    >
      <div className="flex items-baseline justify-between gap-2 text-meta">
        <span className="text-muted-foreground">{stale ? '고치기 전 점수' : '광고 점수'}</span>
        <span className={cn('font-semibold tabular-nums', TEXT[tone])}>{score.toFixed(1)}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted" role="meter" aria-label="광고 점수" aria-valuemin={0} aria-valuemax={10} aria-valuenow={score}>
        <div className={cn('h-full rounded-full', FILL[tone])} style={{ width: `${Math.min(100, Math.max(2, score * 10))}%` }} />
      </div>
      <div className="text-micro text-muted-foreground">{stale ? '다시 판정하지 않았다' : `${attempts}회 시도`}</div>
    </div>
  )
}

function ReviewCard({ draft: d }: { draft: Draft }) {
  const open = useOpenDetail()
  const actions = usePostActions()
  const toast = useToast()
  const targets = useTargets().data ?? []
  const campaign = useCampaigns().data?.items.find((c) => c.ID === d.CampaignID)
  const [dialog, setDialog] = useState<'reject' | 'manual' | 'edit' | null>(null)
  const meta = parseMeta(d)
  const coupang = coupangMetaOf(d)
  const subreddit = String(meta.subreddit ?? '')
  const title = String(meta.title ?? '')
  const mediaId = parseMediaIds(d)[0]
  const account = targets.find((t) => t.id === d.TargetID)?.name
  const pending = d.Status === 'pending_review'
  const auto = autoApproves(d, campaign)
  const turnedDown = d.Status === 'judge_rejected' || d.Status === 'rejected'
  const stale = judgeIsStale(d)
  const placeholder = d.Status === 'judge_rejected' && (d.EditsCount ?? 0) === 0 && d.BodyText.startsWith('(judge rejected')

  const approve = async () => {
    try {
      const res = await actions.approve.mutateAsync({ id: d.ID })
      toast.success(res.scheduled_at ? `승인했다 — ${formatShort(res.scheduled_at)} 슬롯을 받았다` : '승인했다')
    } catch (e) {
      toast.error(errorText(e))
    }
  }

  const revert = async () => {
    try {
      await actions.revertDraft.mutateAsync(d.ID)
      toast.success('검토 대기로 되돌렸다')
    } catch (e) {
      toast.error(errorText(e))
    }
  }

  return (
    <article className="space-y-3 rounded-lg border border-border bg-card p-4">
      <header className="flex items-start gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-meta text-muted-foreground">
            {/* The platform mark is the preview's first line; once is enough. */}
            {subreddit && <span className="font-medium text-foreground">r/{subreddit}</span>}
            <span className="truncate">{subreddit ? '· ' : ''}{d.CampaignName || `캠페인 ${d.CampaignID}`}</span>
            {!pending && <StatusPill domain="post" status={d.Status} />}
            {auto && <AutoApproveNote />}
            {(d.EditsCount ?? 0) > 0 && <span>· 손봄 {d.EditsCount}회</span>}
          </div>
        </div>
        {!coupang && <JudgeScore score={d.JudgeScore} attempts={d.JudgeAttempts} stale={stale} />}
      </header>

      {coupang ? (
        <CoupangPost meta={coupang} body={d.BodyText} account={account} compact />
      ) : (
        <PostPreview
          platform={d.Platform}
          title={title}
          body={d.BodyText}
          channel={subreddit}
          account={account}
          mediaId={mediaId}
          compact
          className="border-0 bg-transparent p-0"
        />
      )}

      {((d.JudgeReason && !coupang) || d.RejectionReason) && (
        <dl className="space-y-1 border-t border-border pt-3 text-table">
          {d.JudgeReason && !coupang && (
            <div className="flex gap-2">
              <dt className="w-16 shrink-0 text-muted-foreground">판정 사유</dt>
              <dd className="min-w-0">{d.JudgeReason}</dd>
            </div>
          )}
          {d.RejectionReason && !pending && (
            <div className="flex gap-2">
              <dt className="w-16 shrink-0 text-muted-foreground">반려 사유</dt>
              <dd className="min-w-0">{d.RejectionReason}</dd>
            </div>
          )}
        </dl>
      )}

      <footer className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
        {pending && (
          <Button
            size="sm"
            variant="primary"
            icon={Check}
            loading={actions.approve.isPending}
            title={auto ? '기다려도 자동으로 승인된다. 지금 누르면 바로 게시 시각을 받는다.' : '캠페인 페이스에 맞춰 게시 시각이 배정된다'}
            onClick={() => void approve()}
          >
            승인
          </Button>
        )}
        <Button size="sm" icon={PenLine} onClick={() => setDialog('edit')}>
          수정
        </Button>
        {turnedDown && (
          <Button
            size="sm"
            variant="primary"
            icon={RotateCcw}
            loading={actions.revertDraft.isPending}
            disabled={placeholder}
            title={placeholder ? '본문이 심사 탈락 안내 글 그대로다. 먼저 수정한다.' : '검토 대기로 되돌린다. 다시 승인하면 슬롯을 받는다.'}
            onClick={() => void revert()}
          >
            검토로 되돌리기
          </Button>
        )}
        {pending && (
          <Button size="sm" icon={X} onClick={() => setDialog('reject')}>
            반려
          </Button>
        )}
        {pending && (
          <Button size="sm" variant="ghost" onClick={() => setDialog('manual')} title="직접 올린 글이면 주소를 남기고 게시됨으로 둔다">
            수동 게시 표시
          </Button>
        )}
        <Button size="sm" variant="ghost" icon={PanelRightOpen} className="ml-auto" onClick={() => open({ kind: 'draft', id: d.ID })}>
          열기
        </Button>
      </footer>

      <Dialog open={dialog === 'edit'} onClose={() => setDialog(null)} title="글 수정" size="lg">
        <div className="max-h-[70vh] overflow-y-auto pb-2">
          <DraftEditor draft={d} onSaved={() => setDialog(null)} onCancel={() => setDialog(null)} />
        </div>
      </Dialog>
      {dialog === 'reject' && <RejectDialog id={d.ID} onClose={() => setDialog(null)} />}
      {dialog === 'manual' && <ManualDialog id={d.ID} platform={d.Platform} onClose={() => setDialog(null)} />}
    </article>
  )
}

function RejectDialog({ id, onClose }: { id: number; onClose: () => void }) {
  const actions = usePostActions()
  const toast = useToast()
  const [reason, setReason] = useState('')
  return (
    <Dialog
      open
      onClose={onClose}
      title="반려"
      description="사유는 비워 둬도 된다. 적으면 초안에 남는다."
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="danger"
            loading={actions.reject.isPending}
            onClick={async () => {
              try {
                await actions.reject.mutateAsync({ id, reason: reason.trim() })
                toast.success('반려했다')
                onClose()
              } catch (e) {
                toast.error(errorText(e))
              }
            }}
          >
            반려
          </Button>
        </>
      }
    >
      <Field label="사유">
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} autoFocus />
      </Field>
    </Dialog>
  )
}

function ManualDialog({ id, platform, onClose }: { id: number; platform: string; onClose: () => void }) {
  const actions = usePostActions()
  const toast = useToast()
  const [url, setUrl] = useState('')
  const problem = postUrlError(url)
  return (
    <Dialog
      open
      onClose={onClose}
      title="수동 게시 표시"
      description={`${platform}에 직접 올린 글의 주소. 게시됨으로 바뀌고 결과에 잡힌다.`}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="primary"
            disabled={!url.trim() || !!problem}
            loading={actions.markDraftManual.isPending}
            onClick={async () => {
              try {
                await actions.markDraftManual.mutateAsync({ id, url: url.trim() })
                toast.success('게시됨으로 표시했다')
                onClose()
              } catch (e) {
                toast.error(errorText(e))
              }
            }}
          >
            게시됨으로 표시
          </Button>
        </>
      }
    >
      <Field label="글 주소" required error={problem}>
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" autoFocus />
      </Field>
    </Dialog>
  )
}
